import { Component, OnDestroy, OnInit } from '@angular/core';
import { timesheetMockData } from '../../../core/constants/report-menu';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ExportReports,
  ExportXLSData,
  ReportMenu,
  ReportStatus,
  ReportStatusAPIData,
} from '../../../core/interface/export-reports';
import { exportReportsFields } from '../../../core/constants/export-reports';
import { ReportsService } from '../../../core/service/reports.service';
import { SessionStorageService } from '../../../core/service/session-storage';
import { Subscription } from 'rxjs/internal/Subscription';
import { DataService } from '../../../core/service/data_service';
import { Router } from '@angular/router';
import { timesheetReport } from '../../../core/constants/constants';

@Component({
  standalone: false,
  selector: 'app-timesheet-reports',
  templateUrl: './timesheet-reports.component.html',
  styleUrl: './timesheet-reports.component.less',
})
export class TimesheetReportsComponent implements OnInit, OnDestroy {
  timesheetData: ReportMenu[] = timesheetMockData;
  validateForm!: FormGroup;
  formFields: ExportReports[] = exportReportsFields;
  timesheetReport = timesheetReport;
  categories: ReportStatus[] = [];
  facEAMMode: string = 'MX';
  isVisible = false;
  showLoader = false;
  showStatusLoader = false;
  facId!: string;
  facilityIdSubscription!: Subscription;
  getReportStatusList!: Subscription;
  exportXLSData!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: Router,
    private readonly reportsService: ReportsService,
    private readonly session: SessionStorageService,
    private readonly dataService: DataService
  ) {}

  ngOnInit(): void {
    this.validateForm = this.fb.group({
      selectAll: [false],
      wdfrom: ['', Validators.required],
      wdto: ['', Validators.required],
      origfrom: [''],
      origto: [''],
      apprfrom: [''],
      apprto: [''],
      projnbr: [''],
      sonbr: [''],
      vendor: [''],
      etype: [''],
      ctype: [''],
      category: this.fb.array([]),
    });

    this.category.valueChanges.subscribe(() => {
      this.updateSelectAllCheckbox();
    });

    this.facilityIdSubscription = this.dataService.getFacilityId.subscribe({
      next: (fac_id: string) => {
        this.facId = fac_id;
      },
      error: (error) => {
        console.error('Error in fetching facility id :', error);
      },
    });

    if (!this.facId) {
      this.facId = this.session.getItem('userAccess')
        ? this.session.getItem('userAccess')['faC_ID']
        : '';
    }
    this.setDefaultDates();
  }

  trackByFn(item: any): any {
    return item.code;
  }

  get category(): FormArray {
    return this.validateForm.get('category') as FormArray;
  }

  setDefaultDates(): void {
    if (
      !this.validateForm.value.wdfrom &&
      !this.validateForm.value.apprfrom &&
      !this.validateForm.value.origfrom
    ) {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);

      this.validateForm.patchValue({
        wdfrom: fromDate.toISOString().split('T')[0],
      });
    }

    if (
      !this.validateForm.value.wdTo &&
      !this.validateForm.value.apprto &&
      !this.validateForm.value.origto
    ) {
      const toDate = new Date();
      this.validateForm.patchValue({
        wdto: toDate.toISOString().split('T')[0],
      });
    }
  }

  loadReportStatus() {
    this.showStatusLoader = true;
    this.category.clear();
    this.getReportStatusList = this.reportsService
      .getReportStatusList()
      .subscribe({
        next: (dataList: ReportStatusAPIData[]) => {
          if (dataList && dataList.length > 0) {
            dataList.forEach((x: ReportStatusAPIData) => {
              const group = this.fb.group({
                selected: false,
                name: x.tsStatus,
              });
              this.category.push(group);
            });
          }
        },
        error: (error) => {
          console.log('Error in loadReportStatus method. Error: ', error);
        },
        complete: () => {
          this.showStatusLoader = false;
        },
      });
  }

  toggleSelectAll(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.category.controls.forEach((control: any) => {
      control.get('selected').setValue(isChecked);
    });
  }

  onCheckboxChange() {
    this.updateSelectAllCheckbox();
  }

  updateSelectAllCheckbox() {
    const allSelected = this.category.controls.every(
      (control: any) => control.get('selected').value
    );

    if (this.validateForm != null) {
      this.validateForm
        ?.get('selectAll')
        ?.setValue(allSelected, { emitEvent: false });
    }
  }

  showModal(item: string): void {
    if (item == 'TS_200') {
      this.loadReportStatus();
      this.isVisible = true;
    } else if (item == 'TS_300') {
      this.route.navigate(['/variance-report']);
    }
  }

  handleCancel(): void {
    this.isVisible = false;
  }

  submitForm(): void {
    this.showLoader = true;
    const selectedCategories = this.validateForm.value.category
      .filter((category: any) => category.selected)
      .map((category: any) => `//${category.name}//`)
      .join(',');

    if (this.facEAMMode != 'MX') {
      alert('This Feature Is Not Available For Passport Data...');
      return;
    }

    let data: ExportXLSData = {
      reportType: 'MX_XPORT_TSLABOR',
      flgArchive: 0,
      wdFrom: this.validateForm.value.wdfrom,
      wdTo: this.validateForm.value.wdto,
      origFrom: this.validateForm.value.origfrom,
      origiTo: this.validateForm.value.origto,
      apprFrom: this.validateForm.value.apprfrom,
      apprTo: this.validateForm.value.apprto,
      project: this.validateForm.value.projnbr,
      soNum: this.validateForm.value.sonbr,
      vender: this.validateForm.value.vendor,
      etype: this.validateForm.value.etype,
      ctype: this.validateForm.value.ctype,
      rStatus: selectedCategories,
      facId: this.facId,
    };

    this.exportXLSData = this.reportsService.exportXLSData(data).subscribe({
      next: (response: any) => {
        const contentType = response.headers.get('content-type');
        const blob = new Blob([response.body], {
          type: contentType,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'TS_200_Report.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Export to Excel failed', error);
        alert(
          'Operation Failed...\n\nBe Sure You Do Not Have The Last Export Spreadsheet Open.'
        );
      },
      complete: () => {
        this.showLoader = false;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.facilityIdSubscription) {
      this.facilityIdSubscription.unsubscribe();
    }

    if (this.getReportStatusList) {
      this.getReportStatusList.unsubscribe();
    }

    if (this.exportXLSData) {
      this.exportXLSData.unsubscribe();
    }
  }
}
